import { useRouter } from "next/router";
import { useCallback, useEffect } from "react";
import { mutate } from "swr";
import { useClearPolicyAutosaves } from "./policyAutosave";
import { UserData } from "./types";
import useData from "./useData";
import createPersistedState from "./createPersistedState";

type UseUserResult =
  | {
      user: UserData;
      isLoading: false;
      logout: () => Promise<void>;
      refresh: () => void;
    }
  | {
      user: null;
      isLoading: true;
      logout: () => Promise<void>;
      refresh: () => void;
    };

export const useUserLoggedIn = createPersistedState<boolean | null>(
  "user_logged_in"
);

export function useOptionalUser() {
  const { data: user, refresh, error } = useData<UserData>("/web/me");
  const clearPolicyAutosaves = useClearPolicyAutosaves();
  const [userLoggedIn, setUserLoggedIn] = useUserLoggedIn(false);

  if (user && !userLoggedIn) setUserLoggedIn(true);

  const logout = useCallback(async () => {
    await fetch("/web/logout");
    // Reset user and tenant state to undefined
    refresh(undefined);
    mutate("/web/tenants", undefined);
    clearPolicyAutosaves();
    setUserLoggedIn(false);
  }, [refresh, clearPolicyAutosaves]);

  return {
    user: user ? user : null,
    isLoading: !user && !error,
    logout,
    error,
    refresh,
    userLoggedIn,
  };
}

// Note: useUser is just like useOptionalUser, but redirects to / if user is
// unauthenticated
/**
 * Gets the current logged in user, or redirects to `/`
 *
 * @param force If true, then `user` should ALWAYS be set (this basically tells
 * typescript that you know what you're doing and there will be a user). If you
 * don't pass `force`, then `user` is possibly null (indicating a loading state)
 */
function useUser(force: true): {
  user: UserData;
  isLoading: false;
  logout: () => Promise<void>;
  refresh: () => void;
};
function useUser(): UseUserResult;
function useUser(_force?: boolean): UseUserResult {
  const { user, logout, error, refresh } = useOptionalUser();

  const router = useRouter();

  useEffect(() => {
    if (error) {
      console.warn("Error fetching current user:", error);
      // User is not authenticated -- make sure the state is cleared and
      // redirect to the login page.
      logout().then(() => router.replace("/"));
    }
  }, [error]);

  if (!user) {
    return { user: null, isLoading: true, logout, refresh };
  } else {
    return { user, isLoading: false, logout, refresh };
  }
}

export default useUser;
